import React, { useEffect, useState } from "react";
import {
  Container,
  Card,
  Row,
  Col,
  Button,
  Table,
  Form,
} from "react-bootstrap";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import PersonIcon from "@mui/icons-material/Person";
import SearchIcon from "@mui/icons-material/Search";
import UndoIcon from '@mui/icons-material/Undo';
import { uwinAPI } from "../../../services/uwin.api";
import { toast } from "react-toastify";
import { decryptOpenSSLAES, safeDecrypt } from "./uwin-decrypt";
import Drawer from "@mui/material/Drawer";
import MemberDrawer from "./MemberDrawer";
import { encryptText, getUwinClientToken, isTestingDateOlderThan30Days } from "../../../helpers/common-uwinhelper.js";
import PregnantWomanIcon from "@mui/icons-material/PregnantWoman";
import ChildCareIcon from '@mui/icons-material/ChildCare';
import PersonAddAlt1Icon from '@mui/icons-material/PersonAddAlt1';
import Loader from "../../../components/Loader";
import { useDispatch } from "react-redux";
import { triggerSessionRefresh } from "../uwinSlice";
// MemberVaccinationContext wraps VaccinationContainer configured for the full
// member vaccination flow (Current + Previous tabs, both PW and CHILD types).
// Previously this imported MemberVaccination.jsx (the old PW-only form) because
// the correct context wrapper did not exist yet.  Now that VaccinationContainer
// is the unified component, this import points to the proper context wrapper.
import MemberVaccinationContext from "./VaccinationModule/contexts/MemberVaccinationContext";
import SearchMembers from "./SearchMembers.jsx";
import ConfirmMobileDialog from "./ConfirmMobileDialog.jsx";
import ConfirmationDialog from "./ConfirmationDialog.jsx";
import VaccinationReviewCard from "./VaccinationReviewCard";
import VaccinationSuccessCard from "./VaccinationSuccessCard";
import VaccinationRejectionCard from "./VaccinationRejectionCard";
import ArrowForwardIosIcon from '@mui/icons-material/ArrowForwardIos';
import SessionSummaryDialog from "./SessionSummaryDialog";
import "./uwin.css";
import { Box, TablePagination } from "@mui/material";
import {
  formatAgeRangeDisplay,
  formatRelativeDateDisplay,
  formatSessionNameDisplay,
  saveBlobToFile,
  calculateAge,
} from "../../../helpers/common-uwinhelper";
import FileDownloadIcon from "@mui/icons-material/FileDownload";
import { CircularProgress } from "@mui/material";
import { useBeneficiaryNavigation } from "./useBeneficiaryNavigation";
import DeclarePregnancy from "./DeclarePregnancy.jsx";

const isIneligibleBeneficiary = (item) => {
  const isFemale = item?.beneficiary_gender?.charAt(0)?.toUpperCase() === 'F';
  const isAge18Plus = Number(item?.age) >= 18;
  const isNotPregnant = !item?.is_pregnant_woman;
  return isFemale && isAge18Plus && isNotPregnant;
};


const SessionDetails = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useDispatch();

  const [isPageLoading, setIsPageLoading] = useState(false);
  const { navigateToDetails } = useBeneficiaryNavigation(setIsPageLoading);

  const [declarePregnancyModal, setDeclarePregnancyModal] = useState(false);
  const [declarePregnancyBen, setDeclarePregnancyBen] = useState(null);
  const [declarePregnancyProfile, setDeclarePregnancyProfile] = useState(null);
  const [lastSearchValue, setLastSearchValue] = useState("");

  // Guard: if there is no location.state (e.g. the user navigated directly to
  // this URL by typing it in the browser) send them back.
  //
  // IMPORTANT: This MUST be in a useEffect, never in the render function body.
  // Calling navigate() directly during render is an anti-pattern — React can
  // re-render this component for many reasons (state updates, context changes,
  // parent re-renders). Any state update inside SessionDetails (e.g. advancing
  // the verified flow stepper) triggers a re-render, which would execute a
  // bare navigate() call again and redirect the user away mid-flow.
  //
  // useEffect runs AFTER the render cycle completes and only when the
  // dependency actually changes, making it safe for navigation side-effects.
  useEffect(() => {
    if (!location.state) {
      navigate(-1);
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps
  // Empty deps: we only want this guard to run once on mount, not on every
  // re-render. location.state will not change after the page loads.

  const sessionId = location.state?.sessionId;
  const [activeTab, setActiveTab] = useState("scheduled");

  const [sessionInfo, setSessionInfo] = useState(null);

  const [beneficiaries, setBeneficiaries] = useState({
    scheduled: [],
    verified: [],
    vaccinated: [],
    rejected: [],
    noshow: [],
  });
  const [drawerComponent, setDrawerComponent] = useState('memberDrawer');
  const [isSearchMode, setIsSearchMode] = useState(false);
  const [previousTab, setPreviousTab] = useState("scheduled");
  const [searchResults, setSearchResults] = useState([]);
  const [searchResetSignal, setSearchResetSignal] = useState(0);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [selectedMobile, setSelectedMobile] = useState("");
  const [confirmItem, setConfirmItem] = useState(null);
  const [startSessionModalOpen, setStartSessionModalOpen] = useState(false);
  const [endSessionModalOpen, setEndSessionModalOpen] = useState(false);
  const [sessionSummaryModalOpen, setSessionSummaryModalOpen] = useState(false);
  const [sessionSummaryData, setSessionSummaryData] = useState(null);

  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  // ================= VERIFIED FLOW STEPPER STATE =================
  const [verifiedFlowStep, setVerifiedFlowStep] = useState(1); // 1: Form, 2: Review, 3a: Success, 3b: Rejection
  const [vaccinationFormData, setVaccinationFormData] = useState(null);
  const [vaccinationResult, setVaccinationResult] = useState(null);
  const [downloadingId, setDownloadingId] = useState(null);
  const [undoConfirmOpen, setUndoConfirmOpen] = useState(false);
  const [undoItem, setUndoItem] = useState(null);

  const isSessionEnded = sessionInfo?.session_status === 3;

  const tabs = [
    {
      key: "scheduled",
      label: "Scheduled",
      count: beneficiaries?.scheduled?.length,
    },
    {
      key: "verified",
      label: "Verified",
      count: beneficiaries?.verified?.length,
    },
    {
      key: "vaccinated",
      label: "Vaccinated",
      count: beneficiaries?.vaccinated?.length,
    },
    {
      key: "rejected",
      label: "Rejected",
      count: beneficiaries?.rejected?.length,
    },
    ...(isSessionEnded ? [
      {
        key: "noshow",
        label: "No Show",
        count: beneficiaries?.noshow?.length,
      }
    ] : [])
  ];

  const [open, setOpen] = useState(false);
  const [selectedBeneficiary, setSelectedBeneficiary] = useState(null);

  const toggleDrawer = (newOpen, beneficiary = null) => () => {
    setOpen(newOpen);

    if (newOpen && beneficiary) {
      // Merge beneficiary data with session information
      const enrichedBeneficiary = {
        ...beneficiary,
        session_site_id: sessionInfo?.session_site_id,
        session_id: sessionInfo?.session_id,
      };
      setSelectedBeneficiary(enrichedBeneficiary);
      setDrawerComponent(activeTab === 'verified' ? 'memberVaccination' : 'memberDrawer');

      // Reset verified flow if opening verified tab
      if (activeTab === 'verified') {
        resetVerifiedFlow();
      }
    } else if (!newOpen) {
      setSelectedBeneficiary(null);
      // Always reset both the stepper state and the drawer component type on
      // close, regardless of which tab is active.
      //
      // Previously this only reset when activeTab === 'verified', which meant:
      //   1. verifiedFlowStep could be stale (e.g. 2, '3a', '3b') the next
      //      time the drawer opened from the scheduled tab via handleSwitchToVaccination.
      //   2. drawerComponent stayed as 'memberVaccination' after the flow ended,
      //      which broke the onClose guard on the next drawer open.
      resetVerifiedFlow();
      setDrawerComponent('memberDrawer');
      benficiaryData();
    }
  };

  const handleMemberSearch = async ({ searchBy, searchValue }) => {
    setPreviousTab(activeTab);
    setIsSearchMode(true);
    setActiveTab("allocate");
    setLastSearchValue(searchValue);
    await searchBeneficiary({ searchBy, searchValue });
  };

  const handleSearchCancel = () => {
    setIsSearchMode(false);
    setActiveTab(previousTab);
    setSearchResults([]);
    setLastSearchValue("");
  };
  const visibleTabs = isSearchMode
    ? [{ key: "allocate", label: "Allocate", count: searchResults.length }]
    : tabs;

  const tableData = activeTab === "allocate" ? searchResults : beneficiaries[activeTab];

  useEffect(() => {
    setPage(0);
  }, [activeTab]);

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const paginatedData = tableData?.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage) || [];

  useEffect(() => {
    if (!sessionId) return;
    benficiaryData();
  }, [sessionId]);

  // ================= VERIFIED FLOW NAVIGATION HANDLERS =================

  // Reset verified flow state when opening drawer
  const resetVerifiedFlow = () => {
    setVerifiedFlowStep(1);
    setVaccinationFormData(null);
    setVaccinationResult(null);
  };

  // Handle vaccination form completion (Step 1 → Step 2)
  const handleVaccinationFormComplete = (formData) => {
    console.log('Vaccination form completed with data:', formData);
    setVaccinationFormData(formData);
    setVerifiedFlowStep(2); // Go to review step
  };

  // Handle vaccination success (Step 2 → Step 3a)
  const handleVaccinationSuccess = (result) => {
    console.log('Vaccination success with result:', result);
    setVaccinationResult(result);
    setVerifiedFlowStep('3a'); // Go to success step
  };

  // Handle vaccination reject navigation (Step 2 → Step 3b)
  const handleVaccinationReject = () => {
    console.log('Navigate to vaccination rejection');
    setVerifiedFlowStep('3b'); // Go to rejection step
  };

  // Handle rejection completion (Step 3b → Close)
  const handleRejectionComplete = (rejectionData) => {
    console.log('Vaccination rejected with data:', rejectionData);
    // Refresh data and close drawer
    benficiaryData();
    setOpen(false);
    resetVerifiedFlow();
  };

  // Handle success completion (Step 3a → Close)  
  const handleSuccessComplete = () => {
    console.log('Vaccination success completed');
    // Refresh data and close drawer
    benficiaryData();
    setOpen(false);
    resetVerifiedFlow();
  };

  const handleDownloadAcknowledgement = async (beneficiary) => {
    try {
      setDownloadingId(beneficiary?.beneficiary_id);

      if (!beneficiary?.beneficiary_reference_id) {
        toast.error("Beneficiary reference ID not found");
        return;
      }

      const payload = {
        beneficiary_ref_id: beneficiary?.beneficiary_reference_id
      };

      const blob = await uwinAPI.downloadMcpCardAcknowledgement(payload);
      const fileName = `${beneficiary?.ben_name}-ReportCard`;
      const success = saveBlobToFile(blob, fileName, "pdf");

      if (success) {
        toast.success("Acknowledgement downloaded successfully!");
      } else {
        toast.error("Failed to download certificate");
      }
    } catch (error) {
      toast.error("An error occurred during downloading certificate");
    } finally {
      setDownloadingId(null);
    }
  };

  const handleUndoVaccinationConfirm = async () => {
    if (!undoItem) return;

    try {
      setIsPageLoading(true);

      const response = await uwinAPI.undoVaccination({
        session_id: sessionId,
        ben_id: undoItem.beneficiary_id,
      });

      if (response?.success) {
        toast.success(response?.message || "Vaccination undone successfully");
        setUndoConfirmOpen(false);
        setUndoItem(null);
        await benficiaryData();
      } else {
        toast.error(response?.message || "Failed to undo vaccination");
        setUndoConfirmOpen(false);
      }
    } catch (error) {
      const errorMessage = error?.response?.data?.message || "Failed to undo vaccination";
      toast.error(errorMessage);
      setUndoConfirmOpen(false);
    } finally {
      setIsPageLoading(false);
    }
  };

  // Handle back navigation in verified flow
  const handleVerifiedFlowBack = () => {
    if (verifiedFlowStep === 2) {
      setVerifiedFlowStep(1); // Back to form
    } else if (verifiedFlowStep === '3b') {
      setVerifiedFlowStep(2); // Back to review
    }
  };
  async function benficiaryData() {

    try {
      setIsPageLoading(true);
      const token = await getUwinClientToken();
      if (!token) {
        toast.error("UWIN token not available");
        return;
      }

      const res = await uwinAPI.sessionBeneficiaryData({
        session_id: sessionId,
      });

      if (res?.success) {
        const result = res.data?.result;
        setSessionInfo(result);

        setBeneficiaries({
          scheduled: result?.scheduled_beneficiaries || [],
          verified: result?.verified_beneficiaries || [],
          vaccinated: result?.vaccinated_beneficiaries || [],
          rejected: result?.rejected_beneficiaries || [],
          noshow: result?.noshow_beneficiaries || [],
          session_site_id: result?.session_site_id,
          session_id: result?.session_id,
          session_status: result?.session_status,
        });
      } else {
        toast.error(res?.message || "Failed to fetch beneficiary data");
      }
    } catch (error) {
      toast.error("Server error while fetching beneficiary data");
    }
    finally {
      setIsPageLoading(false);
    }
  };
  const searchBeneficiary = async (selected_option_) => {
    try {
      setIsPageLoading(true);
      await getUwinClientToken();
      const finalValue = await encryptText(selected_option_.searchValue);
      const dtresponse = await uwinAPI.searchBeneficiary({ search_value: finalValue, search_by: "mobile-number" });
      if (dtresponse && dtresponse.success) {
        setSearchResults(dtresponse.data || []);
      } else {
        toast.error("No records found");
        setSearchResults([]);
      }
    } catch (error) {
      toast.error("Uwin Server error while searching beneficiary");
    } finally {
      setIsPageLoading(false);
    }
  }

  // ================= DATE VALIDATION FOR SESSION START =================

  const canStartSession = () => {
    if (!sessionInfo?.date_of_session) {
      return false;
    }

    try {
      // Parse session date from ISO format: "2025-08-13T00:00:00.000Z"
      const sessionDate = new Date(sessionInfo.date_of_session);
      const currentDate = new Date();

      // Compare dates as strings to avoid time zone issues
      const sessionDateOnly = sessionDate.toDateString();
      const currentDateOnly = currentDate.toDateString();

      // Session can only be started when session date matches current date
      return currentDateOnly === sessionDateOnly;

    } catch (error) {
      return false;
    }
  };

  const shouldShowStartSessionButton = () => {
    if (sessionInfo?.is_campaign_session) return false;
    return sessionInfo?.session_status === 0 && canStartSession();
  };

  // ================= SESSION START/END HANDLERS =================

  /**
   * Handles the start session confirmation and API call
   * Encrypts session_id and session_site_id before sending to API
   * Refreshes beneficiary data on success to update session status
   */
  const handleStartSession = async () => {
    try {
      setIsPageLoading(true);

      // Call start session API
      const response = await uwinAPI.startSession({
        session_id: sessionInfo?.session_id,
        session_site_id: sessionInfo?.session_site_id,
      });

      // Handle response based on success flag
      if (response?.success) {
        toast.success(response?.message || "Session started successfully");

        // Close confirmation modal immediately on success
        setStartSessionModalOpen(false);

        // Refresh beneficiary data to update session status
        await benficiaryData();
        dispatch(triggerSessionRefresh());
      } else {
        // API returned error response
        toast.error(response?.message || "Failed to start session");
        setStartSessionModalOpen(false);
      }

    } catch (error) {
      // Handle different error scenarios
      const status = error?.response?.status;
      const errorMessage = error?.response?.data?.message;

      if (status === 400) {
        // Bad Request - Session cannot be started (e.g., already active)
        toast.error(errorMessage || "Session cannot be started at this time");
      } else if (status === 401) {
        // Unauthorized - Token expired or invalid
        toast.error("Session expired. Please login again");
      } else if (status === 500) {
        // Server Error
        toast.error("Server error. Please try again later");
      } else {
        // Generic error
        toast.error(errorMessage || "Failed to start session");
      }

      setStartSessionModalOpen(false);
    } finally {
      setIsPageLoading(false);
    }
  };

  /**
   * Handles the end session confirmation and API call
   * Encrypts session_id before sending to API
   * Refreshes beneficiary data on success to update session status
   */
  const handleEndSession = async () => {
    try {
      setIsPageLoading(true);

      // Call end session API
      const response = await uwinAPI.endSession({
        session_id: sessionInfo?.session_id,
      });

      // Handle response based on success flag
      if (response?.success) {
        toast.success(response?.message || "Session ended successfully");

        // Close confirmation modal immediately on success so it doesn't hang 
        // while data is refreshing or summary is being fetched.
        setEndSessionModalOpen(false);

        // Refresh beneficiary data to update session status
        await benficiaryData();
        dispatch(triggerSessionRefresh());

        // Fetch session summary and open modal
        try {
          // Format date to YYYY-MM-DD
          const sessionDateStr = sessionInfo?.date_of_session;
          const formattedDate = sessionDateStr
            ? new Date(sessionDateStr).toISOString().split('T')[0]
            : "";

          const summaryPayload = {
            session_id: sessionInfo?.session_id,
            session_date: formattedDate
          };

          const summaryRes = await uwinAPI.sessionSummary(summaryPayload);
          if (summaryRes?.success) {
            const resultData = summaryRes?.data?.result || summaryRes?.data;
            setSessionSummaryData(resultData);
            setSessionSummaryModalOpen(true);
          } else {
            toast.error(summaryRes?.message || "Failed to fetch session summary");
          }
        } catch (summaryErr) {
          toast.error("Error fetching session summary");
        }
      } else {
        // API returned error response
        toast.error(response?.message || "Failed to end session");
        setEndSessionModalOpen(false);
      }

    } catch (error) {
      // Handle different error scenarios
      const status = error?.response?.status;
      const errorMessage = error?.response?.data?.message;

      if (status === 400) {
        // Bad Request - Session cannot be ended (e.g., not active, has pending vaccinations)
        toast.error(errorMessage || "Session cannot be ended at this time");
      } else if (status === 401) {
        // Unauthorized - Token expired or invalid
        toast.error("Session expired. Please login again");
      } else if (status === 500) {
        // Server Error
        toast.error("Server error. Please try again later");
      } else {
        // Generic error
        toast.error(errorMessage || "Failed to end session");
      }

      setEndSessionModalOpen(false);
    } finally {
      setIsPageLoading(false);
    }
  };

  const handleSwitchToVaccination = () => {
    // Always reset the stepper before switching the drawer content.
    // Without this, a stale verifiedFlowStep (e.g. 2, '3a', '3b' from a
    // previous verified-tab flow) prevents MemberVaccinationContext from
    // rendering because the verifiedFlowStep === 1 guard would be false.
    resetVerifiedFlow();

    // Refresh beneficiary data so that the member moves from 'scheduled' 
    // to 'verified' in the background lists before the drawer switches 
    // to the vaccination flow.
    benficiaryData();

    setDrawerComponent('memberVaccination');
  };

  const showConfirmationModal = (item) => {
    const mobile = safeDecrypt(item.ben_mobile_number);
    setSelectedMobile(mobile);
    setConfirmOpen(true);
    setConfirmItem(item);
  }

  const handleConfirmMobile = async () => {
    if (!confirmItem) return;

    try {
      setIsPageLoading(true);
      await getUwinClientToken();

      const encryptedBeneficiaryId = await encryptText(
        confirmItem.beneficiary_id
      );

      await uwinAPI.addBenTooGoingSession({
        session_id: sessionId,
        beneficiary_id: encryptedBeneficiaryId,
      });

      // Close modal immediately on success
      setConfirmOpen(false);
      setConfirmItem(null);

      setIsSearchMode(false);
      setActiveTab("scheduled");
      setSearchResults([]);
      setSearchResetSignal((prev) => prev + 1);
      benficiaryData();
      toast.success("Beneficiary successfully added to session");

    } catch (error) {
      setConfirmOpen(false);
      setConfirmItem(null);
      if (!error?.response?.data?.success) {
        const errorMessage = error.response?.data?.message?.error
          || error.response?.data?.message
          || "Failed to add beneficiary to session";
        toast.error(errorMessage);
        return;
      }
      toast.error("UWIN server error while adding beneficiary");
    } finally {
      setIsPageLoading(false);
    }
  };

  const handleOpenDeclarePregnancy = async (item, e) => {
    e.stopPropagation();
    try {
      setIsPageLoading(true);
      await getUwinClientToken();
      const encryptedId = await encryptText(item.beneficiary_id);
      const res = await uwinAPI.getBeneficiaryProfile({ beneficiary_id: encryptedId });
      if (res?.success) {
        setDeclarePregnancyBen(item);
        setDeclarePregnancyProfile(res.data);
        setDeclarePregnancyModal(true);
      } else {
        toast.error("Failed to fetch beneficiary profile details");
      }
    } catch (error) {
      toast.error("Error fetching beneficiary details");
    } finally {
      setIsPageLoading(false);
    }
  };

  const handleOnSiteRegistration = () => {
    navigate(`/uwin-registration`, {
      state: {
        sessionId: sessionId,
        isOnSiteRegistration: true,
      },
    });
  };

  // ================= DISPLAY TRANSFORMATIONS =================
  const displaySessionName = formatSessionNameDisplay(sessionInfo);
  const displayAgeRange = formatAgeRangeDisplay(sessionInfo);
  const displayDate = formatRelativeDateDisplay(sessionInfo?.display_time);

  const isActive = sessionInfo?.session_status === 1;
  const isUpcoming = sessionInfo?.session_status === 0 && !canStartSession();

  return (
    <>
      {isPageLoading && <Loader />}
      <div className=" my-3 mobile_padding">
        <Card className="shadow-sm border-0 rounded-4 mb-3">
          <Card.Body className="px-4 py-3">
            <Row className="align-items-center">
              {/* LEFT COLUMN (VERTICAL CENTER) */}
              <Col>
                <div className="d-flex align-items-center gap-2">

                  <ArrowBackIcon
                    className="cursor-pointer"
                    onClick={() => navigate("/Session", { state: location.state })}
                  />

                  <div className="fw-semibold fs-6 text-dark d-flex align-items-center">

                    <span style={{ fontSize: "1.2rem", fontWeight: 600 }}>
                      {displaySessionName}, {displayAgeRange} ({displayDate})
                    </span>

                    {(isActive || isUpcoming) && (
                      <span
                        className="ms-2"
                        style={{
                          width: 16,
                          height: 16,
                          minWidth: 16,
                          minHeight: 16,
                          flexShrink: 0,
                          borderRadius: "50%",
                          display: "inline-block",
                          verticalAlign: "middle",
                          backgroundColor: isActive ? "#28a745" : "#6c757d",
                        }}
                        title={isActive ? "Active session" : "Upcoming session"}
                      />
                    )}

                  </div>
                </div>
              </Col>

              {/* RIGHT COLUMN */}
              <Col className="text-end">
                <div className="badge bg-success-subtle text-success px-3 py-2 rounded-pill">
                  Slots Available : {sessionInfo?.allowed_beneficiary_seats}/
                  {sessionInfo?.beneficiary_capacity}
                </div>
                <div className="d-flex justify-content-end gap-2 mt-2">
                  {/* <Button size="sm" variant="primary" onClick={() => handleOnSiteRegistration()}>
                    <PersonIcon fontSize="small" /> On-site Registration
                  </Button> */}

                  {/* Start Session button - only show when session_status === 0 AND current date equals session date */}
                  {shouldShowStartSessionButton() && (
                    <Button
                      variant="primary"
                      onClick={() => setStartSessionModalOpen(true)}
                    >
                      Start Session <ArrowForwardIosIcon />
                    </Button>
                  )}
                </div>
              </Col>
            </Row>
          </Card.Body>
        </Card>
        <SearchMembers
          onSearch={handleMemberSearch}
          onCancel={handleSearchCancel}
          resetSignal={searchResetSignal}
        />


        <Card>
          <Card.Body>
            <div className="fw-semibold mb-2">
              MEMBERS IN CURRENT VACCINATION SESSION
            </div>

            <div className="status-tabs mb-3">
              {visibleTabs.map((tab) => (
                <div
                  key={tab.key}
                  className={`status-tab ${activeTab === tab.key ? "active" : ""
                    }`}
                  onClick={() => setActiveTab(tab.key)}
                >
                  {tab.label}
                  <span className="count">{tab.count}</span>
                </div>
              ))}
            </div>
            {/* <Col md={5}>
              <Form.Control
                type="text"
                size="sm"
                placeholder="Search Members"
                className="cusrome-form-control"
              />
            </Col> */}

            <Table responsive hover size="sm" className="session-table">
              <thead>
                <tr>
                  <th>Name</th>
                  <th>Mobile Number</th>
                  <th>ID Proof</th>
                  <th>ID Number</th>
                  <th>Type</th>
                  {activeTab === 'rejected' ? (
                    <th>Reason</th>
                  ) : (sessionInfo?.session_status === 1) ? (
                    <th>Action</th>
                  ) : null}
                </tr>
              </thead>

              <tbody>
                {paginatedData?.length ? (
                  paginatedData.map((item, index) => (
                    <tr
                      key={item.beneficiary_id || index}
                      onClick={() => {
                        if (activeTab === 'scheduled' && isIneligibleBeneficiary(item)) {
                          toast.error("Beneficiary not eligible");
                          return;
                        }
                        if (activeTab === 'rejected' || activeTab === 'vaccinated') {
                          navigateToDetails(item);
                        } else if (activeTab !== 'allocate') {
                          toggleDrawer(true, item)();
                        }
                      }}
                    >
                      <td>{item.ben_name}</td>
                      <td>
                        {item.ben_mobile_number
                          ? safeDecrypt(item.ben_mobile_number)
                          : "-"}
                      </td>
                      <td>
                        {item.photo_id_type
                          ? safeDecrypt(item.photo_id_type)
                          : "-"}
                      </td>
                      <td>
                        {item.photo_id_number
                          ? safeDecrypt(item.photo_id_number)
                          : "-"}
                      </td>
                      <td>{item.current_beneficiary_type_name || item.beneficiary_type_name}</td>
                      {activeTab === 'rejected' ? (
                        <td>
                          {item.vaccination_notdone_reason?.reason || "-"}
                        </td>
                      ) : (sessionInfo?.session_status === 1) ? (
                        <td>
                          {
                            activeTab == 'allocate' && (
                              <div
                                style={{
                                  display: "flex",
                                  alignItems: "center",
                                  gap: "6px",
                                }}
                              >
                                <PersonAddAlt1Icon
                                  onClick={(e) => { showConfirmationModal(item); e.stopPropagation(); }}
                                  fontSize="small"
                                  sx={{ color: "#555555", cursor: "pointer" }}
                                />
                                {!item?.is_pregnant_woman &&
                                  item?.beneficiary_gender === "Female" &&
                                  calculateAge(item?.dob)?.age >= 12 &&
                                  calculateAge(item?.dob)?.age < 50 &&
                                  isTestingDateOlderThan30Days(item?.previous_pregnancy_year) && (
                                    <PregnantWomanIcon
                                      fontSize="medium"
                                      sx={{ color: "#555555ff", cursor: "pointer" }}
                                      onClick={(e) => handleOpenDeclarePregnancy(item, e)}
                                    />
                                  )}
                              </div>
                            )
                          }
                          {
                            activeTab == 'vaccinated' && (
                              <div
                                style={{
                                  display: "flex",
                                  alignItems: "center",
                                  gap: "6px",
                                }}
                              >
                                  <UndoIcon
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      setUndoItem(item);
                                      setUndoConfirmOpen(true);
                                    }}
                                    fontSize="small"
                                    sx={{ color: "#d32f2f", cursor: "pointer" }}
                                    titleAccess="Undo Vaccination"
                                  />
                                {downloadingId === item.beneficiary_id ? (
                                  <CircularProgress size={20} />
                                ) : (
                                  <FileDownloadIcon
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      handleDownloadAcknowledgement(item);
                                    }}
                                    fontSize="small"
                                    sx={{ color: "#1976d2", cursor: "pointer" }}
                                    titleAccess="Download Acknowledgement"
                                  />
                                )}
                              </div>
                            )
                          }
                        </td>
                      ) : null}
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan="6" className="text-center text-muted py-3">
                      No records found
                    </td>
                  </tr>
                )}
              </tbody>
            </Table>
            <TablePagination
              component="div"
              count={tableData?.length || 0}
              page={page}
              onPageChange={handleChangePage}
              rowsPerPage={rowsPerPage}
              onRowsPerPageChange={handleChangeRowsPerPage}
              sx={{
                "& .MuiTablePagination-selectLabel, & .MuiTablePagination-displayedRows": {
                  margin: 0,
                },
              }}
            />
            <Drawer
              open={open}
              // During the verified vaccination flow (stepper) the drawer must
              // NOT close on backdrop click or Escape key.  Switching steps
              // (e.g. step 1 → step 2) causes a large DOM replacement that MUI
              // interprets as an outside-click, firing onClose and closing the
              // drawer before VaccinationReviewCard can render.
              // For all other drawer components (memberDrawer) the normal
              // close-on-backdrop behaviour is preserved.
              onClose={drawerComponent === 'memberVaccination' ? undefined : toggleDrawer(false)}
              disableEscapeKeyDown={drawerComponent === 'memberVaccination'}
              anchor="right"
            >
              {drawerComponent === 'memberVaccination' ? (
                // Verified Flow Stepper - Show different components based on step
                <>
                  {verifiedFlowStep === 1 && (
                    <MemberVaccinationContext
                      beneficiaryData={selectedBeneficiary}
                      toggleDrawer={toggleDrawer}
                      onFormComplete={handleVaccinationFormComplete}
                      isStepperMode={true}
                    />
                  )}

                  {verifiedFlowStep === 2 && (
                    <VaccinationReviewCard
                      beneficiaryData={selectedBeneficiary}
                      vaccinationData={vaccinationFormData}
                      toggleDrawer={toggleDrawer}
                      onBack={handleVerifiedFlowBack}
                      onVaccinateSuccess={handleVaccinationSuccess}
                      onReject={handleVaccinationReject}
                    />
                  )}

                  {verifiedFlowStep === '3a' && (
                    <VaccinationSuccessCard
                      beneficiaryData={selectedBeneficiary}
                      vaccinationData={vaccinationResult}
                      toggleDrawer={toggleDrawer}
                      onComplete={handleSuccessComplete}
                    />
                  )}

                  {verifiedFlowStep === '3b' && (
                    <VaccinationRejectionCard
                      beneficiaryData={selectedBeneficiary}
                      vaccinationData={vaccinationFormData}
                      toggleDrawer={toggleDrawer}
                      onBack={handleVerifiedFlowBack}
                      onRejectSuccess={handleRejectionComplete}
                    />
                  )}
                </>
              ) : (
                <MemberDrawer
                  toggleDrawer={toggleDrawer}
                  beneficiaryData={selectedBeneficiary}
                  onSwitchToVaccination={handleSwitchToVaccination}
                  onComplete={handleSuccessComplete}
                />
              )}
            </Drawer>

            {/* Allocate beneficiary confirmation modal */}
            <ConfirmMobileDialog
              open={confirmOpen}
              mobile={selectedMobile}
              onClose={() => setConfirmOpen(false)}
              onConfirm={handleConfirmMobile}
            />

            {/* Start Session Confirmation Modal */}
            <ConfirmationDialog
              open={startSessionModalOpen}
              onClose={() => setStartSessionModalOpen(false)}
              onConfirm={handleStartSession}
              title="Connfirmation Details!"
              showWarningIcon={false}
              message={
                <>
                  <p style={{ marginBottom: '12px' }}>
                    Are you sure you want to start this session?
                  </p>
                  <div style={{ color: '#757575', fontSize: '14px' }}>
                    <div><strong>Session:</strong> {sessionInfo?.uip_session_category}</div>
                    <div><strong>Time:</strong> {sessionInfo?.display_time}</div>
                  </div>
                </>
              }
              confirmButtonText="Yes"
              cancelButtonText="No"
              confirmButtonVariant="primary"
            />

            {/* End Session Confirmation Modal */}
            <ConfirmationDialog
              open={endSessionModalOpen}
              onClose={() => setEndSessionModalOpen(false)}
              onConfirm={handleEndSession}
              title="Confirm Details!"
              showWarningIcon={false}
              message={
                <>
                  <p style={{ marginBottom: '12px' }}>
                    Are you sure you want to end this session?
                  </p>
                  <div style={{ color: '#757575', fontSize: '14px' }}>
                    <div><strong>Session:</strong> {sessionInfo?.uip_session_category}</div>
                    <div><strong>Time:</strong> {sessionInfo?.display_time}</div>

                  </div>
                </>
              }
              confirmButtonText="Yes"
              cancelButtonText="No"
              confirmButtonVariant="danger"
            />

            <SessionSummaryDialog
              open={sessionSummaryModalOpen}
              onClose={() => setSessionSummaryModalOpen(false)}
              summaryData={sessionSummaryData}
              sessionInfo={sessionInfo}
            />

            {/* Undo Vaccination Confirmation Modal */}
            <ConfirmationDialog
              open={undoConfirmOpen}
              onClose={() => {
                setUndoConfirmOpen(false);
                setUndoItem(null);
              }}
              onConfirm={handleUndoVaccinationConfirm}
              title="Confirm Details"
              showWarningIcon={false}
              confirmButtonText="YES"
              cancelButtonText="NO"
              confirmButtonVariant="primary"
              message={`Are you sure you want to undo vaccination Details of the Session for ${undoItem?.ben_name || ""} ?`}
            />

          </Card.Body>
        </Card>
      </div>
      <Box
        sx={{
          display: "flex",
          justifyContent: "flex-end",
          pr: 6,
        }}
      >
        {/* Show End Session button when session_status = 1 (active) */}
        {sessionInfo?.session_status === 1 && !sessionInfo?.is_campaign_session && (
          <Button
            variant="danger"
            onClick={() => {
              setEndSessionModalOpen(true);
            }}
            className="btn-endsession"
          >
            End Session <ArrowForwardIosIcon />
          </Button>
        )}
      </Box>

      {declarePregnancyModal && (
        <DeclarePregnancy
          isModalOpen={declarePregnancyModal}
          isModalClose={() => {
            setDeclarePregnancyModal(false);
            setDeclarePregnancyBen(null);
            setDeclarePregnancyProfile(null);
          }}
          benProfileData={declarePregnancyBen}
          profileData={declarePregnancyProfile}
          getBeneficiaryProfile={async () => {
            benficiaryData();
            if (lastSearchValue) {
              await searchBeneficiary({ searchValue: lastSearchValue });
            }
          }}
          getPregnancyData={() => {}}
        />
      )}
    </>
  );
};

export default SessionDetails;